import {
    Button,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TableBody,
    TableContainer
} from '@mui/material';
import { ContentWrapper } from '@/components/ContentWrapper';
import CardHeader from '@/components/shared/CardHeader';
import Chip, { chipColors } from '@/components/shared/Chip';
import { useTranslation } from 'react-i18next';
import { SMBold } from '@/typography/Typography';
import { DATE_FORMAT } from '@/lib/constants';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Table,
    TableCell,
    TableCellFirstCell,
    TableCellNoPadding,
    TableFooter,
    TableFooterCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell,
    TableRow
} from '@/components/shared/styles/Table';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAdminCompanies, useAppDispatch } from '@/lib/hooks';
import { FETCH_STATE } from '@/lib/slices/types';
import { AddCompanyForm } from './AddCompanyForm';
import { TableCard } from '@/components/shared/Card';
import { Dropdown } from '@/components/shared/Dropdown';
import { useNavigate } from 'react-router-dom';
import { useFormatting } from '@/utils/formatting';
import { useUser } from '@/contexts/user/UserContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserRoles } from '@/models/company';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import { Validator, useErrorHandling } from '@/utils/errorHandling';
import { DeleteCompanyRequest } from '@/models/payment';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { DeleteCompanyDialog } from './DeleteCompanyDialog';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { AdminCompany, getAdminCompanies } from '@/lib/slices/paymentsSlice';
import { TableSortLabel } from '@/components/shared/styles/Table';

type SortingType = 'asc' | 'desc' | undefined;

export const TableCellNoPaddingWithFontWeight = ({
    fontWeight,
    children
}: {
    fontWeight: any;
    children: any;
}) => {
    return (
        <TableCellNoPadding style={{ fontWeight }} align="right">
            {children}
        </TableCellNoPadding>
    );
};

export const CompaniesPageV2 = () => {
    const { t } = useTranslation();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDialogCompany, setDeleteDialogCompany] =
        useState<AdminCompany>();

    const { companies, status: companiesStatus } = useAdminCompanies();
    const { user } = useUser();

    const navigate = useNavigate();
    const { getBalanceLabel, getBalanceLabelV2, toDecimal, formatDate } =
        useFormatting();

    const handleCompanySelect = (companyId: string) => {
        navigate(`/?changeCompanyId=${companyId}`);
    };
    const { setAlert } = useNotificationsSnackbar();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const { backendErrorHandler } = useErrorHandling();
    const dispatch = useAppDispatch();

    const [nameSortDirection, setNameSortDirection] =
        useState<SortingType>(undefined);
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const [rowCompany, setRowCompany] = useState<AdminCompany>();

    const onDeleteIconClick = (company: AdminCompany) => {
        handleMenuClose();
        dispatch(setLoader(true));
        PaymentsAPIServiceV2.getInstance()
            .sendDeleteCompanyVerificationEmail({
                companyId: company.companyId
            })
            .then(() => {
                setDeleteDialogOpen(true);
                setDeleteDialogCompany(company);
            })
            .catch(
                backendErrorHandler(
                    'Could not send deletion verification code.'
                )
            )
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const [validator, setValidator] = useState(new Validator());

    const sortedCompanies = [...companies].sort((a, b) =>
        a.name?.localeCompare(b.name)
    );

    const onDeleteDialogSubmit = async (
        request: DeleteCompanyRequest
    ): Promise<void> => {
        dispatch(setLoader(true));
        handleMenuClose();
        PaymentsAPIServiceV2.getInstance()
            .deleteCompany(request)
            .then(() => {
                setAlert('Company deleted successfully', 'success');
                setDeleteDialogOpen(false);
                dispatch(getAdminCompanies());
            })
            .catch(
                backendErrorHandler(
                    'Error deleting company: ',
                    (e, errorDetails) => {
                        setValidator(new Validator(errorDetails?.errors));
                    }
                )
            )
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const onDeleteDialogCancel = () => {
        setDeleteDialogOpen(false);
        setDeleteDialogCompany(undefined);
        setValidator(new Validator());
    };

    const handleMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        company: AdminCompany
    ) => {
        setAnchorEl(event.currentTarget);
        setRowCompany(company);
    };

    const sortedUserList = useMemo(() => {
        let list = [...sortedCompanies];

        const sortArr = [
            {
                key: 'name',
                direction: nameSortDirection
            }
        ];

        sortArr.forEach((sortDirection) => {
            if (sortDirection.direction) {
                list.sort((a: any, b: any) => {
                    if (a[sortDirection.key] && b[sortDirection.key]) {
                        return (
                            a[sortDirection.key]!.toString().localeCompare(
                                b[sortDirection.key!]!.toString()
                            ) * (sortDirection.direction === 'asc' ? 1 : -1)
                        );
                    }
                    return 0;
                });
            }
        });

        return list;
    }, [sortedCompanies, nameSortDirection]);

    const handleSort = useCallback(
        (key: SortingType, func: (direction: SortingType) => void) => {
            setNameSortDirection(undefined);

            key === 'asc' ? func('desc') : func('asc');
        },
        [sortedCompanies, nameSortDirection]
    );

    const companiesByCurrency = useMemo(() => {
        const groups: Record<string, AdminCompany[]> = {};
        sortedUserList.forEach((company) => {
            const currency = company.currency;
            if (!currency) return;
            if (!groups[currency]) {
                groups[currency] = [];
            }
            groups[currency].push(company);
        });
        return groups;
    }, [sortedUserList]);

    const currencyOptions = useMemo(
        () =>
            Object.keys(companiesByCurrency)
                .sort()
                .map((c) => ({ label: c, value: c })),
        [companiesByCurrency]
    );

    const [selectedCurrency, setSelectedCurrency] = useState<string>('');

    useEffect(() => {
        if (currencyOptions.length > 0 && !selectedCurrency) {
            setSelectedCurrency(
                currencyOptions.find((o) => o.value === 'DKK')?.value ??
                    currencyOptions[0].value
            );
        }
    }, [currencyOptions]);

    const visibleCompanies = useMemo(
        () =>
            selectedCurrency
                ? (companiesByCurrency[selectedCurrency] ?? [])
                : sortedUserList,
        [selectedCurrency, companiesByCurrency, sortedUserList]
    );

    const visibleTotals = useMemo(
        () =>
            visibleCompanies.reduce(
                (acc, company) => ({
                    employees: acc.employees + company.usersCount,
                    withdrawed: acc.withdrawed + company.withdrawed,
                    pendingTransactions:
                        acc.pendingTransactions +
                        company.pendingTransactionsCount,
                    finalizedTransactions:
                        acc.finalizedTransactions +
                        company.finalizedTransactionsCount,
                    balance: acc.balance + company.balance
                }),
                {
                    employees: 0,
                    withdrawed: 0,
                    pendingTransactions: 0,
                    finalizedTransactions: 0,
                    balance: 0
                }
            ),
        [visibleCompanies]
    );

    const onLoadCompanies = () => {
        dispatch(getAdminCompanies());
    };

    return (
        <ContentWrapper showImpersonationBanner={false}>
            <>
                <TableCard>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        mr={2}
                    >
                        <CardHeader
                            label={t('company_status')}
                            chip={
                                <Chip
                                    icon={<span></span>}
                                    label={
                                        <SMBold color={chipColors.success.text}>
                                            {formatDate(
                                                new Date(),
                                                DATE_FORMAT.MONTH_NAME
                                            )}
                                        </SMBold>
                                    }
                                    color={'success'}
                                />
                            }
                        />
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Dropdown
                                choices={currencyOptions}
                                value={selectedCurrency}
                                onChange={(v) => setSelectedCurrency(v)}
                                style={{ width: '120px' }}
                            />
                            {user?.role === UserRoles.SuperAdmin && (
                                <AddCompanyForm
                                    onAddSuccess={onLoadCompanies}
                                />
                            )}
                        </Stack>
                    </Stack>
                    {companiesStatus === FETCH_STATE.LOADING ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            py={6}
                        >
                            <CircularProgress size={32} />
                        </Stack>
                    ) : (
                        <TableContainer>
                            <Table aria-label="companies table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCellFirstCell>
                                            <TableSortLabel
                                                direction={nameSortDirection}
                                                onClick={() =>
                                                    handleSort(
                                                        nameSortDirection,
                                                        setNameSortDirection
                                                    )
                                                }
                                            >
                                                {t('alias')}
                                            </TableSortLabel>
                                        </TableHeaderCellFirstCell>
                                        <TableHeaderCell align="right">
                                            {t('active_users')}
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            {t('withdrawed')}
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            {t('transactions')}
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            {t('balance')}
                                        </TableHeaderCell>
                                        <TableHeaderCell
                                            width={'40px'}
                                        ></TableHeaderCell>
                                        {user?.role == UserRoles.SuperAdmin && (
                                            <TableHeaderCell
                                                width={'40px'}
                                            ></TableHeaderCell>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleCompanies.map((row) => (
                                        <TableRow
                                            disabled={row.isDisabled}
                                            hoverEffect
                                            key={row.companyId}
                                        >
                                            <TableCellFirstCell>
                                                {row.name}
                                            </TableCellFirstCell>
                                            <TableCell align="right">
                                                {row.usersCount}
                                            </TableCell>
                                            <TableCell align="right">
                                                {getBalanceLabel(
                                                    row.withdrawed,
                                                    row.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <SMBold>
                                                    <span
                                                        style={{
                                                            color:
                                                                row.pendingTransactionsCount >
                                                                row.finalizedTransactionsCount
                                                                    ? 'danger'
                                                                    : 'inherit'
                                                        }}
                                                    >
                                                        {
                                                            row.pendingTransactionsCount
                                                        }
                                                    </span>
                                                    /
                                                    {
                                                        row.finalizedTransactionsCount
                                                    }
                                                </SMBold>
                                            </TableCell>
                                            <TableCellNoPaddingWithFontWeight
                                                fontWeight={
                                                    row.balance > 0
                                                        ? 'bold'
                                                        : 'normal'
                                                }
                                            >
                                                {getBalanceLabelV2(
                                                    row.balance,
                                                    row.currency,
                                                    { showNegativeStyle: true }
                                                )}
                                            </TableCellNoPaddingWithFontWeight>
                                            <TableCellNoPadding>
                                                <IconButton
                                                    style={{
                                                        paddingLeft: '14px',
                                                        paddingRight: '14px'
                                                    }}
                                                    color="primary"
                                                    onClick={() =>
                                                        handleCompanySelect(
                                                            row.companyId
                                                        )
                                                    }
                                                >
                                                    <OpenInNewIcon />
                                                </IconButton>
                                            </TableCellNoPadding>
                                            {user?.role ===
                                                UserRoles.SuperAdmin && (
                                                <TableCellNoPadding>
                                                    <Button
                                                        color="secondary"
                                                        aria-controls={
                                                            open
                                                                ? 'basic-menu'
                                                                : undefined
                                                        }
                                                        aria-haspopup="true"
                                                        aria-expanded={
                                                            open
                                                                ? 'true'
                                                                : undefined
                                                        }
                                                        onClick={(event) =>
                                                            handleMenuClick(
                                                                event,
                                                                row
                                                            )
                                                        }
                                                    >
                                                        <MoreVertIcon />
                                                    </Button>
                                                </TableCellNoPadding>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableFooterCell>
                                            <SMBold>Total</SMBold>
                                        </TableFooterCell>
                                        <TableFooterCell align="right">
                                            <SMBold>
                                                {' '}
                                                {toDecimal(
                                                    visibleTotals.employees
                                                )}
                                            </SMBold>
                                        </TableFooterCell>
                                        <TableFooterCell align="right">
                                            <SMBold>
                                                {selectedCurrency
                                                    ? toDecimal(
                                                          visibleTotals.withdrawed
                                                      )
                                                    : '-'}
                                            </SMBold>
                                        </TableFooterCell>
                                        <TableFooterCell align="right">
                                            <SMBold>
                                                {toDecimal(
                                                    visibleTotals.pendingTransactions
                                                )}
                                                /
                                                {toDecimal(
                                                    visibleTotals.finalizedTransactions
                                                )}
                                            </SMBold>
                                        </TableFooterCell>
                                        <TableFooterCell
                                            align="right"
                                            style={{ paddingRight: 0 }}
                                        >
                                            <SMBold>
                                                {selectedCurrency
                                                    ? getBalanceLabelV2(
                                                          visibleTotals.balance,
                                                          selectedCurrency,
                                                          {
                                                              showNegativeStyle: true
                                                          }
                                                      )
                                                    : '-'}
                                            </SMBold>
                                        </TableFooterCell>
                                        <TableFooterCell></TableFooterCell>
                                        {user?.role ===
                                            UserRoles.SuperAdmin && (
                                            <TableHeaderCell
                                                width={'40px'}
                                            ></TableHeaderCell>
                                        )}
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    )}
                </TableCard>

                <Menu
                    id="company-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button'
                    }}
                    sx={{ boxShadow: 'none' }}
                >
                    <MenuItem onClick={() => onDeleteIconClick(rowCompany!)}>
                        <>Delete Company</>
                    </MenuItem>
                </Menu>
                {deleteDialogCompany && (
                    <DeleteCompanyDialog
                        onClose={onDeleteDialogCancel}
                        onSubmit={onDeleteDialogSubmit}
                        validator={validator}
                        setValidator={setValidator}
                        open={deleteDialogOpen}
                        companyId={deleteDialogCompany.companyId}
                        companyName={deleteDialogCompany.name}
                    />
                )}
            </>
        </ContentWrapper>
    );
};
